/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import Vue from 'vue'
import Router from 'vue-router'
import store from './store/indexIA'
import defaultRoutes from './router/default.js'
import deepmerge from 'deepmerge';
import WyConstants from './library/wy-constants.js';

Vue.use(Router)
const router = new Router({
  routes: defaultRoutes
})

router.checkUnsaveChanges = (callback) => {
  let vue = router.app;
  if(vue.unsaveDataChanages) {        
    vue.$confirm(vue.$t('warning.unsavedDataMessage'), vue.$t('warning.unsavedDataHeader'), {
      showClose: false,
      confirmButtonText: vue.$t('button.cancel'),
      cancelButtonText: vue.$t('button.confirm'),
      customClass: 'wys-message-box wy-unsaved-change-alert',
      confirmButtonClass: 'wy-grey-button',
      cancelButtonClass: 'wy-blue-button',
      type: 'warning'
    }).then(() => {
      // do nothing  
    }).catch(() => { 
      vue.unsaveDataChanages = false;
      if(callback) callback();
    });
  }
  else {
    if(callback) callback();
  }
}

router.logAmplitudeEvent = () => {
  // TODO: remove this when we retire the amplitude API
  // doing nothing
}

router.beforeEach(async (to, from, next) => {
  // remove events listener
  if(router.app.unregisterPageEvent) router.app.unregisterPageEvent(from.name);
  if(router.app.hideNotification) router.app.hideNotification();

  // set current page object name
  let bindModel = to.meta.bindModel;
  if(bindModel) {
    await store.dispatch('setCurrentPage', {pageName: to.name, bindModel: bindModel});
  }

  const productName = window.sessionStorage.getItem(WyConstants.SESSION_KEY_PRODUCT_NAME);
  if(to.query.sid && to.query.sid != "" && 
    (!store.getters.authUser 
      || store.getters.authUser.sessionId != to.query.sid 
      || store.getters.authUser.productName != productName
    )) {
    const authHost = to.query.host || window.parent.location.href;
    const authOptions = to.query.options;
    const sessionId = to.query.sid;
    const authUrl = window.location.href;
    
    await store.dispatch('c1Auth', {authHost: authHost, authOptions: authOptions, sessionId: sessionId, authUrl: authUrl, productName: WyConstants.PRODUCT_NAME_IA});

    next("/default");
  }
  else if(to.path == "/") {
    if(store.getters.isLoggedIn) {
      next("/default");
    }
    else {      
      next();
    }
  }
  else if(to.path == '/default') {
    next();
  }
  else {
    // check user session from the server
    if(!store.getters.authUser || !store.getters.authUser.token) {
      // if session expired, reset user data and send user to login
      store.dispatch('logout', 'sessionExpired');
      if(router.app.showNotificationLocale) {
        router.app.unsaveDataChanages = false;
        router.app.showNotificationLocale("error", "login.sessionExpiredTitle", "login.sessionExpiredMsg");
      }
      next('/');
    }
    else {
      // init pando
      if(!WyConstants.C1_DEV_MODE) {
        await store.dispatch('pendoInit');
      }
      
      // load glogal filters
      if(to.meta.filterAction) {
        await Promise.resolve({ 
          then: (resolve) => {
            setTimeout(async() => { 
              if(router.app.setLoading) router.app.setLoading(true);
              await store.dispatch(to.meta.filterAction, {pageName: to.meta.bindModel});          
              await store.dispatch('initFilterDataSource', {filterNames: to.meta.filterNames, fromPage: from.meta.bindModel, toPage: to.meta.bindModel});
              if(router.app.setLoading) router.app.setLoading(false);

              resolve(true);
            }, 800);
          }
        });

        // find original page name before forward to nodata page
        for(let menuItem of store.getters.config.navMenu.items) {
          if(menuItem.path==to.path) {
            store.commit("setCurrentPageInfo", {title: menuItem.label, tooltip: menuItem.tooltip});
            break;
          }
          else if(menuItem.subItems) {
            let subItem = menuItem.subItems.find(subItem => {
              return subItem.path==to.path;
            });

            if(subItem != null) {
              store.commit("setCurrentPageInfo", {title: subItem.label, subtitle: menuItem.label, tooltip: subItem.tooltip});
              break;
            }
          }
        }

        if(!await store.dispatch('checkHasFilter', {toPage: to.meta.bindModel, toPath: to.path})) {
          store.dispatch('updateDefaultRoutePath', to.path);
          // goto nodata page
          
          router.push({ path: '/nodata' });
          return;
        }
        else if(store.getters.shareState.warning && store.getters.shareState.warningMessages.length > 0) {
          router.app.showNotification("warning", null, store.getters.shareState.warningMessages, {duration: 1000000000, showClose: true, width: 500});
        }
      }

      let defaultPath = null;
      if(store.getters.config && !store.getters.config.isInited) {
        store.getters.config.isInited = true;

        await store.dispatch('fetchAllConfigurations');
        await store.dispatch('fetchCompanyProfileSettings');

        // load default config        
        let companyConfig = store.getters.config;
        let authUser = store.getters.authUser;

        if(authUser.company && authUser.company.systemProperty) {
          let productCode = authUser.company.selectedProduct.productCode;
          let systemProperty = authUser.company.systemProperty[productCode];
          if(systemProperty && Object.keys(systemProperty).length > 0) {
            companyConfig = deepmerge(companyConfig, systemProperty);
          }
        }

        // map menu item with path
        const pathToMenuItem = {};
        store.getters.config.navMenu.items.map(menu => {
          if(menu.path) {
            pathToMenuItem[menu.path] = {
              menuItem: menu
            };
          }
          if(menu.subItems && menu.subItems.length > 0) {
            menu.subItems.map(item => {
              if(item.path) {
                pathToMenuItem[item.path] = {
                  parentItem: menu,
                  menuItem: item
                }
              }
            })
          }
        });

        // get curren company products configs
        const currCompanyProducts = companyConfig.appProducts.filter(prod => authUser.company.products.find(el => (el.productCode >= prod.code && el.productCode <= prod.code+99)));
        currCompanyProducts.map(prod => {
          const appRouter = require(`./router/${prod.key}.js`).default;
          router.addRoutes(appRouter);
        });

        // DEMO: for demo purpose set demo produce code
        const defaultNavMenuConfigs = (store.getters.companyConfig.default || {}).navMenu;
        const demoConfigs = store.getters.companyConfig.demoConfigs || {};
        const currNavMenu = companyConfig.navMenu.items.filter(item => currCompanyProducts.find(el => (el.code==item.productCode || item.permission || demoConfigs.isDemo)));
        companyConfig.navMenu.items = currNavMenu;

        if(defaultNavMenuConfigs) {
          currNavMenu.map(menu => {
            // set default behaviours expand/collapse          
            menu.isExtend = defaultNavMenuConfigs.isExpand || false;
          });
        }

        // check if all subItems visible
        currNavMenu.map(menu => {
          if(menu.subItems) {
            const menuSubItems = menu.subItems.filter(subItem => !subItem.isHidden);
            if(menuSubItems.length == 0) {
              menu.isHidden = true;
            }
          }
        });

        // get current company products menu items
        if(defaultPath == null && store.getters.defaultRoutePath && store.getters.defaultRoutePath != "") {
          for(let menu of currNavMenu.filter(el => !el.isHidden)) {
            if(store.getters.isProductAllow(menu)) {
              if(menu.subItems && menu.subItems.length > 0) {
                menu.isExtend = true;

                for(let subItem of menu.subItems) {
                  if(!subItem.isHidden && store.getters.isRoleAllow(subItem.aclCodes) && subItem.path==store.getters.defaultRoutePath) {
                    defaultPath = store.getters.defaultRoutePath;
                    break;
                  }
                }
              }
              else if(store.getters.isRoleAllow(menu.aclCodes) && menu.path == store.getters.defaultRoutePath) {
                menu.isExtend = true;
                defaultPath = store.getters.defaultRoutePath;
              }
            }

            if(defaultPath != null) break;
          }
        }

        if(defaultPath == null && currNavMenu && currNavMenu.length > 0) {
          for(let menu of currNavMenu.filter(el => !el.isHidden)) {
            if(store.getters.isProductAllow(menu)) {
              if(menu.subItems && menu.subItems.length > 0) {
                menu.isExtend = true;

                for(let subItem of menu.subItems) {
                  if(!subItem.isHidden && store.getters.isRoleAllow(subItem.aclCodes)) {
                    defaultPath = subItem.path;
                    break;
                  }
                }
              }
              else if(store.getters.isRoleAllow(menu.aclCodes)) {
                menu.isExtend = true;
                defaultPath = menu.path
              }
              break;
            }

            if(defaultPath != null) break;
          }
        }

        // set default router page
        store.commit("updateConfig", companyConfig);

        // update nav menu
        // if(!store.state.site.currentNavMenu) {
          store.commit('updateSite', {navMenu: companyConfig.navMenu});
        // }
      }

      if(defaultPath) {
        store.dispatch('updateDefaultRoutePath', defaultPath);
        // goto default page
        router.push({ path: defaultPath })
      }
      else {
        store.dispatch('updateDefaultRoutePath', to.path);
        // alert if there are unsave data
        router.checkUnsaveChanges(next);
      }
    }
  }
})

// alert unsave changes warning when user leave this page
/*window.addEventListener('beforeunload', function (event) {
  let vue = router.app;
  if(vue.unsaveDataChanages) {    
    event.returnValue = vue.$t('warning.unsavedDataMessage');
  }
}, false)*/

export default router
